<template>
  <div>
    <div class="air__utils__heading">
      <div class="row">
        <div class="col-4 col-sm-6">
          <h5>Device Management</h5>
        </div>
        <div class="col-8 col-sm-6 text-right">
          <div class="d-inline-flex flex-row">
            <div class="mr-2">
              <router-link to="/devices/users">
                <a-button size="large" type="default" icon="team">Users Overview</a-button>
              </router-link>
            </div>
            <div>
              <a-button size="large" type="primary" icon="reload" @click="bulkStatusCheck" :loading="isBulkChecking">
                {{ isBulkChecking ? 'Checking...' : 'Check All Status' }}
              </a-button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="isBulkChecking" class="mb-4">
      <a-progress :percent="bulkProgress.total > 0 ? Math.round((bulkProgress.processed / bulkProgress.total) * 100) : 0" status="active" />
      <div class="text-center mt-2">Checking status: {{ bulkProgress.processed }} / {{ bulkProgress.total }}</div>
    </div>

    <div class="row justify-content-center">
      <div class="col-xl-12">
        <div class="card">
          <div class="card-body">
            <a-table
              :columns="columns"
              :dataSource="devices"
              :loading="loading"
              :pagination="false"
              :rowKey="record => record.id"
              size="middle"
            >
              <template slot="slNo" slot-scope="text, record, index">
                {{ index + 1 }}
              </template>

              <template slot="status" slot-scope="text, record">
                <a-tag :color="record.is_online ? 'green' : 'red'">{{ record.is_online ? 'Online' : 'Offline' }}</a-tag>
                <div v-if="!record.is_online && record.last_connected_at" style="font-size: 0.8em; color: #888;">
                  from {{ record.last_connected_at | moment('DD MMM YYYY, hh:mm A') }}
                </div>
              </template>

              <template slot="lastConnected" slot-scope="text">
                <span v-if="text">{{ text | moment('DD MMM YYYY, hh:mm A') }}</span>
                <span v-else class="text-muted">—</span>
              </template>

              <template slot="punches" slot-scope="text">
                <a-tag color="blue">{{ text }}</a-tag>
              </template>

              <template slot="actions" slot-scope="text, record">
                <a-button size="small" type="primary" icon="reload" class="mr-1" @click="checkStatus(record.id)" />
                <a-button size="small" icon="eye" class="mr-1" @click="viewDetails(record)">Details</a-button>
                <router-link :to="`/devices/${record.id}/users`">
                  <a-button size="small" icon="team">Users</a-button>
                </router-link>
              </template>
            </a-table>
          </div>
        </div>
        <div class="text-right">
          <a-pagination
            showSizeChanger
            @showSizeChange="onShowSizeChange"
            :total="devices.length"
            :pageSize.sync="pageSize"
            v-model="current"
          >
          </a-pagination>
        </div>
      </div>
    </div>

    <!-- Details Modal -->
    <a-modal title="Device Details" :visible="detailsVisible" @cancel="detailsVisible = false" :footer="null" width="700px">
      <div v-if="selectedDevice">
        <div class="row">
          <div class="col-md-6">
            <p><strong>Device Name:</strong> {{ selectedDevice.device_name }}</p>
            <p><strong>Service ID:</strong> {{ selectedDevice.service_device_id }}</p>
            <p><strong>Location:</strong> {{ selectedDevice.location }}</p>
            <p><strong>Status:</strong>
              <a-badge :status="selectedDevice.is_online ? 'success' : 'error'" :text="selectedDevice.is_online ? 'Online' : 'Offline'" />
              <span v-if="!selectedDevice.is_online && selectedDevice.last_connected_at" class="text-muted ml-2">
                from {{ selectedDevice.last_connected_at | moment('DD MMM YYYY, hh:mm A') }}
              </span>
            </p>
            <p><strong>Last Connected:</strong> {{ selectedDevice.last_connected_at | moment('DD MMM YYYY, hh:mm A') }}</p>
          </div>
          <div class="col-md-6" v-if="selectedDevice.device_info_cache">
            <h6>CAMS Info</h6>
            <p><strong>Model:</strong> {{ selectedDevice.device_info_cache.DeviceModel || selectedDevice.device_info_cache.Model || 'N/A' }}</p>
            <p v-if="selectedDevice.device_info_cache.Manufacture"><strong>Manufacturer:</strong> {{ selectedDevice.device_info_cache.Manufacture }}</p>
            <p v-if="selectedDevice.device_info_cache.FirmwareVersion"><strong>Firmware:</strong> {{ selectedDevice.device_info_cache.FirmwareVersion }}</p>
            <p>
              <strong>Users:</strong> {{ selectedDevice.device_info_cache.RegisteredUsers || selectedDevice.device_info_cache.UserCount || 0 }}
              <span v-if="selectedDevice.device_info_cache.UserCapacity"> / {{ selectedDevice.device_info_cache.UserCapacity }}</span>
            </p>
            <p v-if="selectedDevice.device_info_cache.RegisteredFaces || selectedDevice.device_info_cache.ReigsteredFingerprints">
              <strong>Biometrics:</strong> Faces: {{ selectedDevice.device_info_cache.RegisteredFaces || 0 }}, Fingerprints: {{ selectedDevice.device_info_cache.ReigsteredFingerprints || 0 }}
            </p>
            <p v-if="selectedDevice.device_info_cache.EndDate">
              <strong>License Valid Till:</strong> {{ selectedDevice.device_info_cache.EndDate }}
            </p>
          </div>
        </div>
      </div>
    </a-modal>

  </div>
</template>

<script>
export default {
  name: 'DeviceManagement',
  data() {
    return {
      devices: [],
      loading: false,
      isBulkChecking: false,
      bulkBatchId: null,
      bulkProgress: { total: 0, processed: 0 },
      bulkInterval: null,
      selectedDevice: null,
      detailsVisible: false,
      current: 1,
      pageSize: 30,
      columns: [
        { title: 'Sl No', key: 'slNo', scopedSlots: { customRender: 'slNo' }, width: 60, align: 'center' },
        { title: 'Name', dataIndex: 'device_name', key: 'device_name', sorter: (a, b) => (a.device_name || '').localeCompare(b.device_name || '') },
        { title: 'Location', dataIndex: 'location', key: 'location', sorter: (a, b) => (a.location || '').localeCompare(b.location || '') },
        { title: 'Status', key: 'is_online', scopedSlots: { customRender: 'status' }, width: 180 },
        { title: 'Last Connected', dataIndex: 'last_connected_at', key: 'last_connected_at', scopedSlots: { customRender: 'lastConnected' }, width: 200 },
        { title: 'Punches Today', dataIndex: 'today_punches_received', key: 'today_punches_received', scopedSlots: { customRender: 'punches' }, align: 'center', width: 120 },
        { title: 'Actions', key: 'actions', scopedSlots: { customRender: 'actions' }, align: 'center', width: 220 },
      ],
    }
  },
  created() {
    this.fetchDevices()
  },
  beforeDestroy() {
    if (this.bulkInterval) clearInterval(this.bulkInterval)
  },
  methods: {
    fetchDevices() {
      this.loading = true
      this.$http.get('/api/devices')
        .then(response => {
          this.devices = response.data
        })
        .catch(() => {
          this.$notification.error({
            message: 'Error',
            description: 'Failed to fetch devices',
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    checkStatus(id) {
      this.$http.get(`/api/devices/${id}/status`)
        .then(() => {
          this.$notification.success({ message: 'Success', description: 'Status updated' })
          this.fetchDevices()
        })
        .catch(() => {
          this.$notification.error({ message: 'Error', description: 'Failed to update status' })
        })
    },
    bulkStatusCheck() {
      this.isBulkChecking = true
      this.$http.post('/api/devices/bulk-status')
        .then(response => {
          this.bulkBatchId = response.data.batchId
          this.bulkProgress.total = response.data.total
          this.bulkProgress.processed = 0

          if (this.bulkBatchId) {
            this.bulkInterval = setInterval(this.pollBulkProgress, 2000)
          } else {
            this.isBulkChecking = false
          }
        })
        .catch(() => {
          this.isBulkChecking = false
          this.$notification.error({ message: 'Error', description: 'Failed to initiate bulk check' })
        })
    },
    pollBulkProgress() {
      if (!this.bulkBatchId) return

      this.$http.get(`/api/devices/bulk-status-progress/${this.bulkBatchId}`)
        .then(response => {
          this.bulkProgress.total = response.data.total
          this.bulkProgress.processed = response.data.processed

          if (response.data.is_completed) {
            clearInterval(this.bulkInterval)
            this.bulkInterval = null
            this.isBulkChecking = false
            this.fetchDevices()
            this.$notification.success({ message: 'Complete', description: 'Bulk status check completed' })
          }
        })
        .catch(() => {
          clearInterval(this.bulkInterval)
          this.bulkInterval = null
          this.isBulkChecking = false
        })
    },
    viewDetails(device) {
      this.selectedDevice = device
      this.detailsVisible = true
    },
    onShowSizeChange(current, pageSize) {
      this.pageSize = pageSize
      this.current = current
    },
  },
}
</script>
